import { validateFood, type Food, type FoodBasis, type FoodMeasure } from "./food";
import type { Macronutrients, NutrientProfile } from "../nutrition/nutrients";
import { validateNutrientProfile } from "../nutrition/nutrients";
import { catalogMicronutrientsOnly } from "../nutrition/nutrient-normalization";
import { requireFiniteNonNegative } from "../shared/validation";

/** Mutable at provider-mapping boundaries; normalized Food values are readonly. */
export interface ExternalMacroData {
  proteinGrams?: number;
  carbohydrateGrams?: number;
  fatGrams?: number;
}

export interface ExternalFoodCandidate {
  readonly provider: string;
  readonly providerFoodId: string;
  readonly name: string;
  readonly brand?: string;
  readonly barcode?: string;
  readonly basis: FoodBasis;
  readonly measures?: readonly FoodMeasure[];
  readonly reportedCalories?: number;
  readonly macros: ExternalMacroData;
  readonly fibreGrams?: number;
  readonly micronutrients?: NutrientProfile["micronutrients"];
}

export interface ImportedFoodDraft {
  readonly candidate: ExternalFoodCandidate;
  readonly proposedNutrients: NutrientProfile;
  /** True when one or more provider macros were absent and explicit user review is mandatory. */
  readonly macroReviewRequired: boolean;
  readonly missingMacroKeys: ReadonlyArray<keyof Macronutrients>;
}

const MACRO_KEYS: ReadonlyArray<keyof Macronutrients> = ["proteinGrams", "carbohydrateGrams", "fatGrams"];

/**
 * Converts provider data into an editable draft. If calories exist but any macro is
 * missing, reported energy is split evenly between protein, carbohydrate and fat as
 * a starting point. It cannot become a Food until the user explicitly reviews it.
 */
export function createImportedFoodDraft(candidate: ExternalFoodCandidate): ImportedFoodDraft {
  if (!candidate.provider.trim() || !candidate.providerFoodId.trim()) throw new Error("Food source identity is required");
  if (!candidate.name.trim()) throw new Error("Imported food name is required");

  const missingMacroKeys = MACRO_KEYS.filter((key) => candidate.macros[key] === undefined);
  const isIncomplete = missingMacroKeys.length > 0;
  if (isIncomplete && candidate.reportedCalories === undefined) {
    throw new Error("Imported food has neither complete macros nor enough calories to propose a macro split");
  }

  if (candidate.reportedCalories !== undefined) requireFiniteNonNegative(candidate.reportedCalories, "Reported calories");
  const oneThirdCalories = (candidate.reportedCalories ?? 0) / 3;
  const proposedMacros: Macronutrients = isIncomplete
    ? {
        proteinGrams: oneThirdCalories / 4,
        carbohydrateGrams: oneThirdCalories / 4,
        fatGrams: oneThirdCalories / 9,
      }
    : {
        proteinGrams: candidate.macros.proteinGrams!,
        carbohydrateGrams: candidate.macros.carbohydrateGrams!,
        fatGrams: candidate.macros.fatGrams!,
      };

  return {
    candidate,
    missingMacroKeys,
    macroReviewRequired: isIncomplete,
    proposedNutrients: validateNutrientProfile({
      macros: proposedMacros,
      fibreGrams: candidate.fibreGrams,
      micronutrients: catalogMicronutrientsOnly(candidate.micronutrients ?? {}),
    }),
  };
}

export interface ConfirmImportInput {
  readonly id: string;
  readonly draft: ImportedFoodDraft;
  readonly finalNutrients: NutrientProfile;
  readonly userReviewedMacros: boolean;
  readonly now: string;
}

export function confirmImportedFood(input: ConfirmImportInput): Food {
  if (input.draft.macroReviewRequired && !input.userReviewedMacros) {
    throw new Error("Incomplete provider macros must be reviewed before this food can be imported");
  }
  const finalNutrients = validateNutrientProfile({
    ...input.finalNutrients,
    micronutrients: catalogMicronutrientsOnly(input.finalNutrients.micronutrients),
  });
  return validateFood({
    id: input.id,
    name: input.draft.candidate.name.trim(),
    brand: input.draft.candidate.brand?.trim() || undefined,
    barcode: input.draft.candidate.barcode,
    basis: input.draft.candidate.basis,
    ...(input.draft.candidate.measures === undefined ? {} : { measures: input.draft.candidate.measures }),
    nutrients: finalNutrients,
    provenance: {
      kind: "imported",
      provider: input.draft.candidate.provider,
      providerFoodId: input.draft.candidate.providerFoodId,
      importedAt: input.now,
    },
    preferred: true,
    createdAt: input.now,
    updatedAt: input.now,
  });
}
